import React, { useState } from 'react';
import { Heart, CheckCircle, User, MapPin, Phone, Mail, Calendar, Target, Smile, Shield } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const IntakeForm = () => {
  const [formData, setFormData] = useState({
    // Personal Information
    fullName: '',
    dateOfBirth: '',
    address: '',
    phoneNumber: '',
    email: '',
    
    // Background
    whyCoaching: '',
    previousExperience: '',
    previousExperienceDetails: '',
    spiritualBackground: '',
    
    // Where Are You Now (1-10 scales)
    faithLife: 5,
    emotionalWellbeing: 5,
    relationships: 5,
    purposeCalling: 5,
    confidenceSelfWorth: 5,
    stressPeaceBalance: 5,
    
    // Goals & Expectations
    focusArea1: '',
    focusArea2: '',
    focusArea3: '',
    successfulOutcomes: '',
    
    // Strengths & Challenges
    strengths: '',
    challenges: '',
    
    // Commitment & Availability
    weeklyCommitment: '',
    preferredTimes: '',
    sessionPreference: '',
    
    // Agreement
    agreementSignature: '',
    agreementDate: new Date().toISOString().split('T')[0]
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  const handleInputChange = (e) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'range' ? parseInt(value) : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');

    // Validate required fields
    const requiredFields = ['fullName', 'email', 'whyCoaching', 'agreementSignature'];
    const missingFields = requiredFields.filter(field => !formData[field]);
    
    if (missingFields.length > 0) {
      setSubmitMessage('Please fill in all required fields including your digital signature.');
      setIsSubmitting(false);
      return;
    }

    try {
      const response = await fetch(`${API}/intake-form`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const result = await response.json();

      if (response.ok) {
        setSubmitMessage('✅ Thank you! Your intake form has been submitted successfully. I\'ll review your information before our session and send you any additional details via email.');
        // Reset form
        setFormData({
          fullName: '', dateOfBirth: '', address: '', phoneNumber: '', email: '',
          whyCoaching: '', previousExperience: '', previousExperienceDetails: '', spiritualBackground: '',
          faithLife: 5, emotionalWellbeing: 5, relationships: 5, purposeCalling: 5, confidenceSelfWorth: 5, stressPeaceBalance: 5,
          focusArea1: '', focusArea2: '', focusArea3: '', successfulOutcomes: '',
          strengths: '', challenges: '',
          weeklyCommitment: '', preferredTimes: '', sessionPreference: '',
          agreementSignature: '', agreementDate: new Date().toISOString().split('T')[0]
        });
      } else {
        setSubmitMessage(`❌ ${result.detail || 'Something went wrong. Please try again.'}`);
      }
    } catch (error) {
      console.error('Intake form error:', error);
      setSubmitMessage('❌ Unable to submit form. Please try again or contact us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const RatingScale = ({ label, name, value }) => (
    <div style={{ marginBottom: '20px' }}>
      <label className="body-regular" style={{ display: 'block', marginBottom: '8px', color: '#5D4037' }}>
        <strong>{label}</strong>
      </label>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <span style={{ fontSize: '12px', color: '#8B4513' }}>Struggling</span>
        <input
          type="range"
          name={name}
          min="1"
          max="10"
          value={value}
          onChange={handleInputChange}
          style={{
            flex: 1,
            height: '6px',
            background: `linear-gradient(to right, #CD853F 0%, #CD853F ${(value-1)*11.11}%, #D2B48C ${(value-1)*11.11}%, #D2B48C 100%)`,
            borderRadius: '3px',
            outline: 'none',
            cursor: 'pointer'
          }}
        />
        <span style={{ fontSize: '12px', color: '#8B4513' }}>Thriving</span>
        <div style={{ 
          minWidth: '30px', 
          textAlign: 'center', 
          background: '#8B4513', 
          color: 'white', 
          padding: '4px 8px', 
          borderRadius: '4px',
          fontSize: '12px',
          fontWeight: 'bold'
        }}>
          {value}
        </div>
      </div>
    </div>
  );

  return (
    <div style={{ 
      background: 'linear-gradient(135deg, #FFF8DC 0%, #F5F5DC 100%)',
      minHeight: '100vh',
      padding: '40px 20px'
    }}>
      <div style={{ maxWidth: '800px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '48px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', marginBottom: '24px' }}>
            <Heart size={32} style={{ color: '#8B4513' }} />
            <h1 className="hero-medium" style={{ color: '#8B4513' }}>
              KHesed+™ Life Coaching
            </h1>
          </div>
          <h2 className="heading-1" style={{ color: '#8B4513', marginBottom: '8px' }}>
            Client Intake Form
          </h2>
          <p className="body-regular" style={{ color: '#5D4037', fontStyle: 'italic' }}>
            Repairing the breach, and restoring lives on Truth.
          </p>
        </div>

        <form onSubmit={handleSubmit} style={{
          background: 'rgba(255, 248, 220, 0.8)',
          padding: '40px',
          borderRadius: '0px',
          border: '2px solid #D2B48C'
        }}>
          
          {/* Personal Information */}
          <section style={{ marginBottom: '48px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
              <User size={24} style={{ color: '#8B4513' }} />
              <h3 className="heading-2" style={{ color: '#8B4513' }}>Personal Information</h3>
            </div>
            
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px' }}>
              <div>
                <label className="body-regular" style={{ display: 'block', marginBottom: '8px', color: '#5D4037' }}>
                  Full Name *
                </label>
                <input
                  type="text"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleInputChange}
                  required
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '1px solid #D2B48C',
                    borderRadius: '0px',
                    background: 'white',
                    fontSize: '14px',
                    fontFamily: 'Crimson Text, serif'
                  }}
                />
              </div>

              <div>
                <label className="body-regular" style={{ display: 'block', marginBottom: '8px', color: '#5D4037' }}>
                  Date of Birth
                </label>
                <input
                  type="date"
                  name="dateOfBirth"
                  value={formData.dateOfBirth}
                  onChange={handleInputChange}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '1px solid #D2B48C',
                    borderRadius: '0px',
                    background: 'white',
                    fontSize: '14px',
                    fontFamily: 'Crimson Text, serif'
                  }}
                />
              </div>

              <div style={{ gridColumn: '1 / -1' }}>
                <label className="body-regular" style={{ display: 'block', marginBottom: '8px', color: '#5D4037' }}>
                  Address
                </label>
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '1px solid #D2B48C',
                    borderRadius: '0px',
                    background: 'white',
                    fontSize: '14px',
                    fontFamily: 'Crimson Text, serif'
                  }}
                />
              </div>

              <div>
                <label className="body-regular" style={{ display: 'block', marginBottom: '8px', color: '#5D4037' }}>
                  Phone Number
                </label>
                <input
                  type="tel"
                  name="phoneNumber"
                  value={formData.phoneNumber}
                  onChange={handleInputChange}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '1px solid #D2B48C',
                    borderRadius: '0px',
                    background: 'white',
                    fontSize: '14px',
                    fontFamily: 'Crimson Text, serif'
                  }}
                />
              </div>

              <div>
                <label className="body-regular" style={{ display: 'block', marginBottom: '8px', color: '#5D4037' }}>
                  Email *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '1px solid #D2B48C',
                    borderRadius: '0px',
                    background: 'white',
                    fontSize: '14px',
                    fontFamily: 'Crimson Text, serif'
                  }}
                />
              </div>
            </div>
          </section>

          {/* Background */}
          <section style={{ marginBottom: '48px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
              <CheckCircle size={24} style={{ color: '#8B4513' }} />
              <h3 className="heading-2" style={{ color: '#8B4513' }}>Background</h3>
            </div>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <div>
                <label className="body-regular" style={{ display: 'block', marginBottom: '8px', color: '#5D4037' }}>
                  What brings you to life coaching at this time? *
                </label>
                <textarea
                  name="whyCoaching"
                  value={formData.whyCoaching}
                  onChange={handleInputChange}
                  required
                  rows="4"
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '1px solid #D2B48C',
                    borderRadius: '0px',
                    background: 'white',
                    fontSize: '14px',
                    fontFamily: 'Crimson Text, serif',
                    resize: 'vertical'
                  }}
                />
              </div>

              <div>
                <label className="body-regular" style={{ display: 'block', marginBottom: '12px', color: '#5D4037' }}>
                  Have you worked with a coach, counselor, or therapist before?
                </label>
                <div style={{ display: 'flex', gap: '20px', marginBottom: '12px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="previousExperience"
                      value="yes"
                      checked={formData.previousExperience === 'yes'}
                      onChange={handleInputChange}
                      style={{ accentColor: '#8B4513' }}
                    />
                    <span className="body-regular" style={{ color: '#5D4037' }}>Yes</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="previousExperience"
                      value="no"
                      checked={formData.previousExperience === 'no'}
                      onChange={handleInputChange}
                      style={{ accentColor: '#8B4513' }}
                    />
                    <span className="body-regular" style={{ color: '#5D4037' }}>No</span>
                  </label>
                </div>
                
                {formData.previousExperience === 'yes' && (
                  <textarea
                    name="previousExperienceDetails"
                    value={formData.previousExperienceDetails}
                    onChange={handleInputChange}
                    placeholder="Please share briefly..."
                    rows="2"
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '1px solid #D2B48C',
                      borderRadius: '0px',
                      background: 'white',
                      fontSize: '14px',
                      fontFamily: 'Crimson Text, serif',
                      resize: 'vertical'
                    }}
                  />
                )}
              </div>

              <div>
                <label className="body-regular" style={{ display: 'block', marginBottom: '8px', color: '#5D4037' }}>
                  What is your spiritual background/faith journey?
                </label>
                <textarea
                  name="spiritualBackground"
                  value={formData.spiritualBackground}
                  onChange={handleInputChange}
                  rows="3"
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '1px solid #D2B48C',
                    borderRadius: '0px',
                    background: 'white',
                    fontSize: '14px',
                    fontFamily: 'Crimson Text, serif',
                    resize: 'vertical'
                  }}
                />
              </div>
            </div>
          </section>

          {/* Where Are You Now */}
          <section style={{ marginBottom: '48px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
              <Target size={24} style={{ color: '#8B4513' }} />
              <h3 className="heading-2" style={{ color: '#8B4513' }}>Where Are You Now?</h3>
            </div>
            <p className="body-regular" style={{ color: '#5D4037', marginBottom: '24px' }}>
              Please use the sliders to reflect your current experience in each area:
            </p>
            
            <div style={{
              background: 'rgba(139, 69, 19, 0.05)',
              padding: '24px',
              borderRadius: '8px',
              border: '1px solid #D2B48C'
            }}>
              <RatingScale label="Faith/Spiritual Life" name="faithLife" value={formData.faithLife} />
              <RatingScale label="Emotional Well-being" name="emotionalWellbeing" value={formData.emotionalWellbeing} />
              <RatingScale label="Relationships" name="relationships" value={formData.relationships} />
              <RatingScale label="Purpose/Calling" name="purposeCalling" value={formData.purposeCalling} />
              <RatingScale label="Confidence/Self-Worth" name="confidenceSelfWorth" value={formData.confidenceSelfWorth} />
              <RatingScale label="Stress/Peace Balance" name="stressPeaceBalance" value={formData.stressPeaceBalance} />
            </div>
          </section>

          {/* Goals & Expectations */}
          <section style={{ marginBottom: '48px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
              <Smile size={24} style={{ color: '#8B4513' }} />
              <h3 className="heading-2" style={{ color: '#8B4513' }}>Goals & Expectations</h3>
            </div>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <div>
                <label className="body-regular" style={{ display: 'block', marginBottom: '12px', color: '#5D4037' }}>
                  What are the top 3 areas of your life you would like to focus on?
                </label>
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div>
                    <label className="body-small" style={{ display: 'block', marginBottom: '4px', color: '#8B4513', fontWeight: '600' }}>
                      1.
                    </label>
                    <input
                      type="text"
                      name="focusArea1"
                      value={formData.focusArea1}
                      onChange={handleInputChange}
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        border: '1px solid #D2B48C',
                        borderRadius: '0px',
                        background: 'white',
                        fontSize: '14px',
                        fontFamily: 'Crimson Text, serif'
                      }}
                    />
                  </div>
                  
                  <div>
                    <label className="body-small" style={{ display: 'block', marginBottom: '4px', color: '#8B4513', fontWeight: '600' }}>
                      2.
                    </label>
                    <input
                      type="text"
                      name="focusArea2"
                      value={formData.focusArea2}
                      onChange={handleInputChange}
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        border: '1px solid #D2B48C',
                        borderRadius: '0px',
                        background: 'white',
                        fontSize: '14px',
                        fontFamily: 'Crimson Text, serif'
                      }}
                    />
                  </div>
                  
                  <div>
                    <label className="body-small" style={{ display: 'block', marginBottom: '4px', color: '#8B4513', fontWeight: '600' }}>
                      3.
                    </label>
                    <input
                      type="text"
                      name="focusArea3"
                      value={formData.focusArea3}
                      onChange={handleInputChange}
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        border: '1px solid #D2B48C',
                        borderRadius: '0px',
                        background: 'white',
                        fontSize: '14px',
                        fontFamily: 'Crimson Text, serif'
                      }}
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="body-regular" style={{ display: 'block', marginBottom: '8px', color: '#5D4037' }}>
                  What outcomes would make coaching successful for you?
                </label>
                <textarea
                  name="successfulOutcomes"
                  value={formData.successfulOutcomes}
                  onChange={handleInputChange}
                  rows="3"
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '1px solid #D2B48C',
                    borderRadius: '0px',
                    background: 'white',
                    fontSize: '14px',
                    fontFamily: 'Crimson Text, serif',
                    resize: 'vertical'
                  }}
                />
              </div>
            </div>
          </section>

          {/* Strengths & Challenges */}
          <section style={{ marginBottom: '48px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
              <Heart size={24} style={{ color: '#8B4513' }} />
              <h3 className="heading-2" style={{ color: '#8B4513' }}>Strengths & Challenges</h3>
            </div>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <div>
                <label className="body-regular" style={{ display: 'block', marginBottom: '8px', color: '#5D4037' }}>
                  What do you see as your strengths?
                </label>
                <textarea
                  name="strengths"
                  value={formData.strengths}
                  onChange={handleInputChange}
                  rows="3"
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '1px solid #D2B48C',
                    borderRadius: '0px',
                    background: 'white',
                    fontSize: '14px',
                    fontFamily: 'Crimson Text, serif',
                    resize: 'vertical'
                  }}
                />
              </div>

              <div>
                <label className="body-regular" style={{ display: 'block', marginBottom: '8px', color: '#5D4037' }}>
                  What challenges or obstacles are you currently facing?
                </label>
                <textarea
                  name="challenges"
                  value={formData.challenges}
                  onChange={handleInputChange}
                  rows="3"
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '1px solid #D2B48C',
                    borderRadius: '0px',
                    background: 'white',
                    fontSize: '14px',
                    fontFamily: 'Crimson Text, serif',
                    resize: 'vertical'
                  }}
                />
              </div>
            </div>
          </section>

          {/* Commitment & Availability */}
          <section style={{ marginBottom: '48px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
              <Calendar size={24} style={{ color: '#8B4513' }} />
              <h3 className="heading-2" style={{ color: '#8B4513' }}>Commitment & Availability</h3>
            </div>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <div>
                <label className="body-regular" style={{ display: 'block', marginBottom: '12px', color: '#5D4037' }}>
                  Are you able to commit to weekly/bi-weekly sessions?
                </label>
                <div style={{ display: 'flex', gap: '20px', marginBottom: '12px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="weeklyCommitment"
                      value="yes"
                      checked={formData.weeklyCommitment === 'yes'}
                      onChange={handleInputChange}
                      style={{ accentColor: '#8B4513' }}
                    />
                    <span className="body-regular" style={{ color: '#5D4037' }}>Yes</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="weeklyCommitment"
                      value="no"
                      checked={formData.weeklyCommitment === 'no'}
                      onChange={handleInputChange}
                      style={{ accentColor: '#8B4513' }}
                    />
                    <span className="body-regular" style={{ color: '#5D4037' }}>No</span>
                  </label>
                </div>
                
                <div>
                  <label className="body-regular" style={{ display: 'block', marginBottom: '8px', color: '#5D4037' }}>
                    Preferred days/times:
                  </label>
                  <input
                    type="text"
                    name="preferredTimes"
                    value={formData.preferredTimes}
                    onChange={handleInputChange}
                    placeholder="e.g., Weekday evenings, Saturday mornings, etc."
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '1px solid #D2B48C',
                      borderRadius: '0px',
                      background: 'white',
                      fontSize: '14px',
                      fontFamily: 'Crimson Text, serif'
                    }}
                  />
                </div>
              </div>

              <div>
                <label className="body-regular" style={{ display: 'block', marginBottom: '12px', color: '#5D4037' }}>
                  How do you prefer sessions?
                </label>
                <div style={{ display: 'flex', gap: '20px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="sessionPreference"
                      value="in-person"
                      checked={formData.sessionPreference === 'in-person'}
                      onChange={handleInputChange}
                      style={{ accentColor: '#8B4513' }}
                    />
                    <span className="body-regular" style={{ color: '#5D4037' }}>In person</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="sessionPreference"
                      value="online"
                      checked={formData.sessionPreference === 'online'}
                      onChange={handleInputChange}
                      style={{ accentColor: '#8B4513' }}
                    />
                    <span className="body-regular" style={{ color: '#5D4037' }}>Online (Zoom/phone)</span>
                  </label>
                </div>
              </div>
            </div>
          </section>

          {/* Life Coaching vs Therapy Info */}
          <section style={{ marginBottom: '48px' }}>
            <h3 className="heading-2" style={{ color: '#8B4513', marginBottom: '16px' }}>
              Life Coaching vs. Therapy
            </h3>
            
            <div style={{
              background: 'rgba(139, 69, 19, 0.05)',
              padding: '24px',
              borderRadius: '8px',
              border: '1px solid #D2B48C'
            }}>
              <p className="body-regular" style={{ color: '#5D4037', marginBottom: '16px', lineHeight: '1.6' }}>
                <strong>Life coaching</strong> is a forward-focused process designed to help you set and achieve personal and spiritual goals. 
                Coaching emphasizes growth, accountability, and practical strategies to move from where you are now toward where you want to be.
              </p>
              
              <p className="body-regular" style={{ color: '#5D4037', marginBottom: '16px', lineHeight: '1.6' }}>
                <strong>Therapy</strong>, on the other hand, is a clinical practice provided by licensed mental health professionals. 
                Therapy often focuses on diagnosing and treating mental health conditions, healing past trauma, and addressing psychological symptoms.
              </p>
              
              <p className="body-regular" style={{ color: '#5D4037', lineHeight: '1.6' }}>
                While life coaching can be deeply transformative, it is not a substitute for therapy, counseling, or medical treatment. 
                If at any point your needs fall outside the scope of coaching, I will encourage you to seek support from a qualified mental health professional.
              </p>
            </div>
          </section>

          {/* What to Expect */}
          <section style={{ marginBottom: '48px' }}>
            <h3 className="heading-2" style={{ color: '#8B4513', marginBottom: '24px' }}>
              What to Expect from Life Coaching
            </h3>
            
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '24px' }}>
              <div style={{
                background: 'rgba(139, 69, 19, 0.05)',
                padding: '20px',
                borderRadius: '8px',
                border: '1px solid #D2B48C'
              }}>
                <h4 className="heading-3" style={{ color: '#8B4513', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <CheckCircle size={20} style={{ color: '#4CAF50' }} />
                  What a Life Coach Will Do:
                </h4>
                <ul style={{ color: '#5D4037', fontSize: '14px', lineHeight: '1.5', paddingLeft: '20px' }}>
                  <li>Listen with compassion and without judgment</li>
                  <li>Ask thought-provoking questions</li>
                  <li>Help you set goals and stay accountable</li>
                  <li>Encourage growth rooted in biblical truth</li>
                  <li>Provide tools, resources, and strategies for progress</li>
                  <li>Pray with you and invite God's wisdom into the process</li>
                </ul>
              </div>
              
              <div style={{
                background: 'rgba(205, 133, 63, 0.05)',
                padding: '20px',
                borderRadius: '8px',
                border: '1px solid #CD853F'
              }}>
                <h4 className="heading-3" style={{ color: '#8B4513', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ color: '#f44336', fontSize: '20px' }}>❌</span>
                  What a Life Coach Will Not Do:
                </h4>
                <ul style={{ color: '#5D4037', fontSize: '14px', lineHeight: '1.5', paddingLeft: '20px' }}>
                  <li>Diagnose or treat mental health conditions</li>
                  <li>Prescribe medication</li>
                  <li>Focus solely on the past or trauma recovery</li>
                  <li>Replace the role of a licensed counselor or therapist</li>
                  <li>Make decisions for you — coaching empowers you to choose</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Confidentiality & Agreement */}
          <section style={{ marginBottom: '32px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
              <Shield size={24} style={{ color: '#8B4513' }} />
              <h3 className="heading-2" style={{ color: '#8B4513' }}>Confidentiality & Agreement</h3>
            </div>
            
            <div style={{
              background: 'rgba(139, 69, 19, 0.05)',
              padding: '24px',
              borderRadius: '8px',
              border: '2px solid #8B4513',
              marginBottom: '20px'
            }}>
              <p className="body-regular" style={{ color: '#5D4037', marginBottom: '16px', lineHeight: '1.6' }}>
                I understand that life coaching is not therapy and does not replace mental health care. 
                All information shared will remain confidential except where disclosure is required by law.
              </p>
              
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px' }}>
                <div>
                  <label className="body-regular" style={{ display: 'block', marginBottom: '8px', color: '#5D4037' }}>
                    Digital Signature (Type your full name) *
                  </label>
                  <input
                    type="text"
                    name="agreementSignature"
                    value={formData.agreementSignature}
                    onChange={handleInputChange}
                    required
                    placeholder="Type your full name here"
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '2px solid #8B4513',
                      borderRadius: '0px',
                      background: 'white',
                      fontSize: '14px',
                      fontFamily: 'Crimson Text, serif'
                    }}
                  />
                </div>
                
                <div>
                  <label className="body-regular" style={{ display: 'block', marginBottom: '8px', color: '#5D4037' }}>
                    Date
                  </label>
                  <input
                    type="date"
                    name="agreementDate"
                    value={formData.agreementDate}
                    onChange={handleInputChange}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '1px solid #D2B48C',
                      borderRadius: '0px',
                      background: 'white',
                      fontSize: '14px',
                      fontFamily: 'Crimson Text, serif'
                    }}
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Submit Button */}
          <div style={{ textAlign: 'center' }}>
            <button
              type="submit"
              disabled={isSubmitting}
              style={{
                background: '#8B4513',
                borderColor: '#8B4513',
                color: 'white',
                padding: '16px 32px',
                border: '2px solid #8B4513',
                borderRadius: '0px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s ease',
                opacity: isSubmitting ? 0.7 : 1
              }}
              onMouseEnter={(e) => {
                if (!isSubmitting) {
                  e.target.style.background = '#A0522D';
                  e.target.style.borderColor = '#A0522D';
                }
              }}
              onMouseLeave={(e) => {
                if (!isSubmitting) {
                  e.target.style.background = '#8B4513';
                  e.target.style.borderColor = '#8B4513';
                }
              }}
            >
              {isSubmitting ? 'Submitting...' : 'Submit Intake Form'}
            </button>
          </div>

          {/* Success/Error Message */}
          {submitMessage && (
            <div style={{
              marginTop: '24px',
              padding: '20px',
              background: submitMessage.includes('✅') ? 'rgba(76, 175, 80, 0.1)' : 'rgba(244, 67, 54, 0.1)',
              border: `2px solid ${submitMessage.includes('✅') ? '#4CAF50' : '#f44336'}`,
              borderRadius: '8px',
              textAlign: 'center'
            }}>
              <p className="body-regular" style={{ 
                color: submitMessage.includes('✅') ? '#2E7D32' : '#c62828',
                margin: 0,
                lineHeight: '1.5'
              }}>
                {submitMessage}
              </p>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};